import { Button } from '@heroui/react';
import React, { useCallback } from 'react';

import { LogCard } from './shared';
import type { LogCardAccordionItemClasses } from './shared/accordionStyles';
import { useMessageViewMode } from '../hooks/useMessageViewMode';
import { AgentInputMessageConfig } from '../utils/logParser';

import i18n from '~/common/helpers/i18n';
import { Code } from '~/components/Code';

interface MessageCardProps {
  config: AgentInputMessageConfig;
  /** Optional slot rendered inside the log accordion (e.g. annotations). */
  annotationSlot?: React.ReactNode;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  itemKey?: string;
  itemClasses?: LogCardAccordionItemClasses;
  className?: string;
}

/**
 * Component to display agent.input_message in a structured card with format toggle
 */
export function MessageCard({
  config,
  annotationSlot,
  isExpanded,
  onExpandedChange,
  itemKey,
  itemClasses,
  className
}: MessageCardProps) {
  const { viewMode, displayContent, codeLanguage, setViewMode } =
    useMessageViewMode({
      content: config.content
    });

  const handleViewModeChange = useCallback(
    (mode: 'text' | 'markdown' | 'json') => {
      setViewMode(mode);
    },
    [setViewMode]
  );

  const isSystem = config.messageType === 'system';

  const title = (
    <span className='text-sm font-semibold text-on-surface'>
      {isSystem
        ? (i18n.t('scenes.build.execution.logs.message.system') as string)
        : (i18n.t('scenes.build.execution.logs.message.user') as string)}
    </span>
  );
  const textValue = isSystem
    ? (i18n.t('scenes.build.execution.logs.message.system') as string)
    : (i18n.t('scenes.build.execution.logs.message.user') as string);

  return (
    <LogCard
      title={title}
      textValue={textValue}
      footer={annotationSlot}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      itemKey={itemKey}
      itemClasses={itemClasses}
      className={className}
    >
      {/* Format toggle buttons */}
      <div className='mb-3 flex gap-2'>
        <Button
          variant={viewMode === 'text' ? 'solid' : 'light'}
          size='sm'
          color={viewMode === 'text' ? 'primary' : 'default'}
          onPress={() => handleViewModeChange('text')}
          className='text-xs'
        >
          {
            i18n.t(
              'scenes.build.execution.logs.message.view_mode.text'
            ) as string
          }
        </Button>
        <Button
          variant={viewMode === 'markdown' ? 'solid' : 'light'}
          size='sm'
          color={viewMode === 'markdown' ? 'primary' : 'default'}
          onPress={() => handleViewModeChange('markdown')}
          className='text-xs'
        >
          {
            i18n.t(
              'scenes.build.execution.logs.message.view_mode.markdown'
            ) as string
          }
        </Button>
        <Button
          variant={viewMode === 'json' ? 'solid' : 'light'}
          size='sm'
          color={viewMode === 'json' ? 'primary' : 'default'}
          onPress={() => handleViewModeChange('json')}
          className='text-xs'
        >
          {
            i18n.t(
              'scenes.build.execution.logs.message.view_mode.json'
            ) as string
          }
        </Button>
      </div>

      {/* Content display */}
      <div className='rounded border border-outline'>
        {viewMode === 'text' ? (
          <div className='max-h-96 overflow-y-auto p-4 text-sm text-on-surface'>
            <pre className='whitespace-pre-wrap font-sans'>
              {config.content}
            </pre>
          </div>
        ) : (
          <Code
            value={displayContent}
            language={codeLanguage}
            readOnly
            height='200px'
            disableJsonValidation={viewMode === 'json'}
            classNames={{
              wrapper: 'rounded'
            }}
          />
        )}
      </div>
    </LogCard>
  );
}
